import PropTypes from 'prop-types';

// Styles
import styled from 'styled-components';
import classnames from 'classnames';
import colors from 'styles/colors';
import sizes from 'styles/sizes';

import { ENHANCEMENT_CLASS } from 'contexts/CruiseSearchContext/constants';
import { Typography, useTheme } from '@mui/material';

const Container = styled(Typography)`
  &.container {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${colors.secondary.polar};
  }

  &.space{
      margin-right: 30px;
      @media (max-width: ${sizes.media.smallMaxWidth}px) {
        margin-right: 10px;
      }
  }
`;

const Cta = styled.button`
  padding: 0 14px;
  height: 50px;
  border-radius: 5px;
  font-family: inherit;
  font-size: inherit;
  text-transform: ${({ textTransform }) => textTransform};
  .isPricingPanel & {
    margin: 0 16px;
  }
  min-width: inherit;
  .default & {
    margin: 8px 16px 0;
  }

  transition: background-color 0.2s cubic-bezier(0, 0, 0, 0),
    transform 0.1s cubic-bezier(0, 0, 0, 0);

  .full-width & {
    width: 100%;
  }

  .primary & {
    background-color: ${({ palette }) => palette.primary.main};
    color: ${({ palette }) => palette.primary.contrastText};
  }

  .secondary & {
    background-color: ${({ palette }) => palette.primary.contrastText};
    border: 1px solid ${({ palette }) => palette.primary.main};
    color: ${({ palette }) => palette.primary.main};
    padding: 13px 30px;

    &:hover {
      background-color: ${colors.secondary.deepCoveLight};
      border-color: ${colors.secondary.deepCoveLight};
      color: ${colors.primary.ice};
    }
  }

  .container & {
    max-width: 390px;
  }

  .tertiary & {
    color: ${colors.functional.black};
    background-color: ${colors.functional.sun};

    &:hover {
      color: ${colors.primary.ice};
    }
  }

  .enhanced & {
    background: transparent;
    border: 1px solid ${({ palette }) => palette.text.secondary};
    border-radius: 3px;
    color: ${({ palette }) => palette.text.secondary};

    @media (max-width: ${sizes.media.mediumMaxWidth}px) {
      color: ${({ palette }) => palette.secondary.contrastText};
      background-color: ${({ palette }) => palette.secondary.main};
      border: 0px;
      &:active {
        background-color: ${({ palette }) => palette.secondary.dark};
      }
    }
    &:hover {
      background: transparent;
      @media (max-width: ${sizes.media.mediumMaxWidth}px) {
        background: ${({ palette }) => palette.secondary.main};
      }
    }
  }

  .hover & {
    transition: none;
    border-radius: ${({ shape }) => shape.borderRadius};

    &:hover {
      background-color: ${({ palette }) => palette.secondary.main};
      border: none;
      color: ${({ palette }) => palette.secondary.contrastText};
    }

    &:active {
      background-color: ${({ palette }) => palette.secondary.dark};
      color: ${({ palette }) => palette.secondary.contrastText};
      border: none;
    }
  }

  &:active {
    transform: scale(0.98);
  }

  &:hover {
    background-color: ${colors.secondary.deepCoveLight};
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  .small & {
    font-size: 14px;
    line-height: 17px;
    border-radius: 3px;
    height: auto;
    padding: 12px 30px;
  }

  @media (min-width: ${sizes.media.xSmallMaxWidth}px) {
    .default & {
      margin: 12px 24px 0;
    }
    .isPricingPanel & {
      margin: 0 24px;
    }
  }

  @media (min-width: ${sizes.media.modalWidth}px) {
    .default & {
      margin: 16px 32px 0;
    }
    .isPricingPanel & {
      margin: 0 32px;
    }
  }

  .${ENHANCEMENT_CLASS} & {
    line-height: 19px;
    padding: 13px 30px;

    @media (max-width: ${sizes.media.smallMaxWidth}px) {
      line-height: 17px;
      padding: 12px 30px;
    }
  }

  .isPricingPanel & {
    margin: 0 20px;
  }
`;

/**
 * Basic button used throughout the app.
 *
 * @param {string} id
 * @param {bool} container - Used for positioning
 * @param {func} onClick
 * @param {string} className
 * @param {object} passedRef - Used for animation
 * @param {string} children - button text
 * @param {bool} primary - light blue
 * @param {bool} secondary - dark blue
 * @param {bool} tertiary - yellow
 * @param {string} href - if it acts as a link
 * @param {bool} fullWidth - Used for styling (fills width of container)
 * @param {bool} disabled
 */

const Button = ({
  id,
  container,
  onClick,
  className,
  passedRef,
  children,
  primary,
  secondary,
  tertiary,
  enhanced,
  href,
  fullWidth,
  disabled,
  small,
  hover,
  textTransform,
}) => {
  const { palette, shape } = useTheme();
  const classesReceived = className?.split(' ') || [];
  const classesToAdd = {};

  if (className) {
    classesReceived.forEach((item) => {
      if (item) {
        classesToAdd[item] = true;
      }
    });
  }

  const classes = classnames({
    [className]: !!className,
    ...classesToAdd,
    'full-width': fullWidth,
    container,
    primary,
    secondary,
    tertiary,
    enhanced,
    small,
    hover,
  });
  const as = href ? 'a' : undefined;
  return (
    <Container
      id={id}
      className={classes}
      variant="label1SemiBold"
      component="div"
    >
      <Cta
        as={as}
        onClick={onClick}
        ref={passedRef}
        disabled={disabled}
        palette={palette}
        shape={shape}
        textTransform={textTransform}
      >
        {children}
      </Cta>
    </Container>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  container: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  passedRef: PropTypes.object,
  children: PropTypes.any,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  enhanced: PropTypes.bool,
  href: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  small: PropTypes.bool,
  hover: PropTypes.bool,
  textTransform: PropTypes.string,
};

export default Button;
